/**
 * The creator's account setup checklist (IN-1718, Flow 1 steps 3-6).
 *
 * A creator lands in the workspace able to see campaigns but not able to be
 * paid, because the pieces that make payment possible — a linked platform and
 * their financial details — are set up on screens they have to go looking for.
 * This turns that into an ordered list with one next thing to do.
 *
 * Pure: the caller fetches the profile, payout record and invoices and passes
 * them in.
 */

/** Payout fields that must be present before finance can pay anyone. */
const REQUIRED_PAYOUT_FIELDS = [
  'account_holder_name',
  'account_number',
  'ifsc_code',
  'bank_name',
  'pan_card_no',
];

const filled = (value) => String(value ?? '').trim() !== '';

/**
 * Whether the payout record has everything finance needs. GST is optional —
 * plenty of creators are not registered — but a creator who says they are
 * MSME has to give the Udyam number, or the declaration is unusable.
 */
export function payoutComplete(payout) {
  const record = payout || {};
  if (!REQUIRED_PAYOUT_FIELDS.every((field) => filled(record[field]))) return false;
  if (record.is_msme && !filled(record.msme_udyam_number)) return false;
  return true;
}

/**
 * The ordered steps, each with whether it is done and where to go if not.
 * `{ profile, payout, invoices }` — all optional, so a partly-loaded page can
 * still render the list.
 */
export function buildChecklist(data) {
  const { profile, payout, invoices } = data || {};
  const who = profile || {};
  const raised = Array.isArray(invoices) ? invoices.length > 0 : false;

  return [
    {
      id: 'account',
      label: 'Create your account',
      detail: 'Signed in — this is your creator workspace.',
      done: true,
      href: null,
      cta: null,
    },
    {
      id: 'platform',
      label: 'Connect Instagram or YouTube',
      detail:
        'Links your posts to the campaigns you are on, so brands see your performance without you sending screenshots.',
      done: Boolean(who.is_instagram_linked || who.is_youtube_linked),
      href: '/influencer/social-settings',
      cta: 'Connect',
    },
    {
      id: 'payout',
      label: 'Add your financial details',
      detail:
        'Bank account, PAN, and GST or MSME if they apply. Entered once — every invoice after this reuses them.',
      done: payoutComplete(payout),
      href: '/influencer/payout-settings',
      cta: 'Add details',
    },
    {
      id: 'invoice',
      label: 'Raise your first invoice',
      detail: 'Once a campaign is delivered, invoice for it here and follow the payment status.',
      done: raised,
      href: '/influencer/invoices',
      cta: 'Go to invoices',
    },
  ];
}

/** `{done, total, complete, percent}` — percent is rounded, for a progress bar. */
export function checklistProgress(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const total = list.length;
  const done = list.filter((step) => step && step.done).length;
  return {
    done,
    total,
    complete: total > 0 && done === total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
  };
}

/** The first step still outstanding, or null when there is nothing left. */
export function nextStep(steps) {
  const list = Array.isArray(steps) ? steps : [];
  return list.find((step) => step && !step.done) || null;
}
